import type { ExtractedLine } from './types'
import { DESIGN_PAGE } from './text-extractor'

// ============================================================
// Divider auto-suggestion: find the whitespace gap separating the
// header (and footer) from the resume body. Pure + unit-tested.
// ============================================================

export const BAND_SUGGEST = {
  /** A gap qualifies as a band boundary when ≥ max(this, factor × median). */
  minGapPx: 24,
  medianGapFactor: 1.8,
  /** Never suggest a header taller / footer deeper than this. */
  maxBandDepth: 320,
  /** Fallback header depth when no confident gap exists. */
  fallbackHeader: 120,
}

export interface BandSuggestion {
  /** Suggested divider y (design coords): everything above is header. */
  headerY: number
  headerConfident: boolean
  /** Suggested footer divider y: everything below is footer; null = none. */
  footerY: number | null
}

export function suggestBands(
  lines: ExtractedLine[],
  pageH: number = DESIGN_PAGE.h
): BandSuggestion {
  if (lines.length === 0) {
    return {
      headerY: BAND_SUGGEST.fallbackHeader,
      headerConfident: false,
      footerY: null,
    }
  }
  const sorted = [...lines].sort((a, b) => a.bbox.y - b.bbox.y)
  const gaps: Array<{ afterIndex: number; gap: number; y: number }> = []
  for (let i = 0; i < sorted.length - 1; i++) {
    const bottom = sorted[i].bbox.y + sorted[i].bbox.height
    const gap = sorted[i + 1].bbox.y - bottom
    if (gap > 0) gaps.push({ afterIndex: i, gap, y: bottom + gap / 2 })
  }
  const median = medianOf(gaps.map(g => g.gap))
  const threshold = Math.max(
    BAND_SUGGEST.minGapPx,
    BAND_SUGGEST.medianGapFactor * median
  )
  // Dividers hug the band content (small pad into the gap) rather than
  // sitting at the gap midpoint — a huge header→body gap must not drag
  // half the empty space into the band.
  const pad = (gap: number) => Math.min(gap / 2, 18)

  // Header: first qualifying gap scanning from the top; divider sits just
  // below the header cluster's bottom.
  let headerY = BAND_SUGGEST.fallbackHeader
  let headerConfident = false
  for (const g of gaps) {
    const clusterBottom = g.y - g.gap / 2
    if (clusterBottom > BAND_SUGGEST.maxBandDepth) break
    if (g.gap >= threshold) {
      headerY = Math.round(clusterBottom + pad(g.gap))
      headerConfident = true
      break
    }
  }

  // Footer: first qualifying gap scanning from the bottom — only when
  // there is content below it (otherwise there's no footer to capture).
  // Divider sits just above the footer cluster's top.
  let footerY: number | null = null
  for (let i = gaps.length - 1; i >= 0; i--) {
    const g = gaps[i]
    const clusterTop = g.y + g.gap / 2
    if (pageH - clusterTop > BAND_SUGGEST.maxBandDepth) continue
    if (g.gap >= threshold && clusterTop > headerY) {
      const hasContentBelow = sorted.some(l => l.bbox.y >= clusterTop)
      if (hasContentBelow) footerY = Math.round(clusterTop - pad(g.gap))
      break
    }
  }

  return { headerY, headerConfident, footerY }
}

function medianOf(values: number[]): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}
